import React, { useState, useCallback, useRef } from 'react';
import './Timeline.css';
import TimelineBubble from './TimelineBubble.js';
import TimelineHover from './TimelineHover.js';
import TimelineTranscriptHover from './TimelineTranscriptHover.js';
import TimelineSentimentHover from './TimelineSentimentHover.js';

function Timeline({ timelineData }) {
  // Handle both nested (timelineData.data.minutes) and flat (timelineData.minutes) structures
  const data = timelineData?.data || timelineData;
  const minutes = data?.minutes;
  const averageWordsPerMinute = data?.averageWordsPerMinute;
  
  // Transcript hover state
  const [showTranscriptHover, setShowTranscriptHover] = useState(false);
  const [transcriptHoverPosition, setTranscriptHoverPosition] = useState({ x: 0, y: 0 });
  const [currentTranscriptMinute, setCurrentTranscriptMinute] = useState(null);
  const transcriptTimeoutRef = useRef(null);

  // Sentiment hover state
  const [showSentimentHover, setShowSentimentHover] = useState(false);
  const [sentimentHoverPosition, setSentimentHoverPosition] = useState({ x: 0, y: 0 });
  const [currentSentimentMinute, setCurrentSentimentMinute] = useState(null);
  const sentimentTimeoutRef = useRef(null);

  // Helper function to determine if text should be white
  // Since all progress bars now have the same width, we can use a simple threshold
  const shouldUseWhiteText = useCallback((wordCount, totalWords) => {
    if (totalWords === 0) return false;
    const percentage = (wordCount / totalWords) * 100;
    
    // The text is positioned on the right side of the progress bar
    // We'll use 85% as the threshold - if the progress bar extends beyond 85%,
    // it will cover the text position
    return percentage > 85;
  }, []);

  // Transcript hover handlers
  const calculateTranscriptPosition = useCallback((element) => {
    if (!element) return { x: 0, y: 0 };
    
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate center position
    let x = rect.left + rect.width / 2;
    let y = rect.top - 10; // Always position above the element
    
    // Ensure the hover doesn't go off-screen
    const hoverWidth = 500; // Use max-width from CSS to be safe
    const hoverHeight = 300; // estimated height for above positioning
    
    // Calculate minimum and maximum allowed X positions with generous padding
    const minX = hoverWidth / 2 + 30; // Minimum left edge with extra padding
    const maxX = viewportWidth - hoverWidth / 2 - 30; // Maximum right edge with extra padding
    
    // Clamp X position to ensure hover is fully visible
    x = Math.max(minX, Math.min(maxX, x));
    
    // Adjust Y position if it would go off-screen at the top
    if (y - hoverHeight < 10) {
      y = rect.bottom + 10; // Show below if not enough space above
    }
    
    return { x, y };
  }, []);

  const handleTranscriptMouseEnter = useCallback((e, minute) => {
    // Clear any existing timeout
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
    }
    
    const position = calculateTranscriptPosition(e.currentTarget);
    setTranscriptHoverPosition(position);
    setCurrentTranscriptMinute(minute);
    setShowTranscriptHover(true);
  }, [calculateTranscriptPosition]);

  const handleTranscriptMouseLeave = useCallback(() => {
    // Add a small delay to prevent flickering when moving between minute number and hover
    transcriptTimeoutRef.current = setTimeout(() => {
      setShowTranscriptHover(false);
    }, 50);
  }, []);

  const handleTranscriptHoverEnter = useCallback(() => {
    // Clear the timeout when mouse enters the hover
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
    }
  }, []);

  const handleTranscriptHoverLeave = useCallback(() => {
    // Start the timeout when mouse leaves the hover
    transcriptTimeoutRef.current = setTimeout(() => {
      setShowTranscriptHover(false);
    }, 50);
  }, []);

  const handleTranscriptClose = useCallback(() => {
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
    }
    setShowTranscriptHover(false);
  }, []);

  // Sentiment hover handlers
  const calculateSentimentPosition = useCallback((element) => {
    if (!element) return { x: 0, y: 0 };
    
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate center position
    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    
    // Ensure the hover doesn't go off-screen
    const hoverWidth = 500; // Use max-width from CSS to be safe
    const hoverHeight = 300; // estimated height for above positioning
    
    // Calculate minimum and maximum allowed X positions with generous padding
    const minX = hoverWidth / 2 + 30; // Minimum left edge with extra padding
    const maxX = viewportWidth - hoverWidth / 2 - 30; // Maximum right edge with extra padding
    
    // Clamp X position to ensure hover is fully visible
    x = Math.max(minX, Math.min(maxX, x));
    
    // Adjust Y position if it would go off-screen at the top
    if (y - hoverHeight < 10) {
      y = rect.bottom + 10; // Show below if not enough space above
    }
    
    return { x, y };
  }, []);

  const handleSentimentMouseEnter = useCallback((e, minute) => {
    // Clear any existing timeout
    if (sentimentTimeoutRef.current) {
      clearTimeout(sentimentTimeoutRef.current);
    }
    
    const position = calculateSentimentPosition(e.currentTarget);
    setSentimentHoverPosition(position);
    setCurrentSentimentMinute(minute);
    setShowSentimentHover(true);
  }, [calculateSentimentPosition]);

  const handleSentimentMouseLeave = useCallback(() => {
    // Add a small delay to prevent flickering when moving between sentiment pill and hover
    sentimentTimeoutRef.current = setTimeout(() => {
      setShowSentimentHover(false);
    }, 50);
  }, []);

  const handleSentimentHoverEnter = useCallback(() => {
    // Clear the timeout when mouse enters the hover
    if (sentimentTimeoutRef.current) {
      clearTimeout(sentimentTimeoutRef.current);
    }
  }, []);

  const handleSentimentHoverLeave = useCallback(() => {
    // Start the timeout when mouse leaves the hover
    sentimentTimeoutRef.current = setTimeout(() => {
      setShowSentimentHover(false);
    }, 50);
  }, []);

  const handleSentimentClose = useCallback(() => {
    if (sentimentTimeoutRef.current) {
      clearTimeout(sentimentTimeoutRef.current);
    }
    setShowSentimentHover(false);
  }, []);
  
  if (!timelineData || !minutes || minutes.length === 0) {
    return (
      <div className="timeline-empty-state">
        <div className="timeline-empty-icon">
          <i className="ti ti-clock"></i>
        </div>
        <p>No timeline data yet... but it's coming!</p>
      </div>
    );
  }
  
  // Get all unique speakers from all minutes
  const allSpeakers = new Set();
  minutes.forEach(minute => {
    minute.speakerWords.forEach(speaker => {
      allSpeakers.add(speaker.speaker);
    });
  });
  const speakers = Array.from(allSpeakers);

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <div className="timeline-stats">
          <span className="timeline-stat">
            <i className="ti ti-clock"></i>
            {minutes.length} minutes
          </span>
          <span className="timeline-stat">
            <i className="ti ti-users"></i>
            {speakers.length} speakers
          </span>
          <span className="timeline-stat">
            <i className="ti ti-message-circle"></i>
            {averageWordsPerMinute} wpm avg
          </span>
        </div>
      </div>
      
      <div className="timeline-content">
        <div className="timeline-minutes">
          {minutes.map((minute, index) => (
            <div key={minute.minute} className="timeline-minute">
              <div className="minute-header">
                <span 
                  className="minute-number"
                  onMouseEnter={(e) => handleTranscriptMouseEnter(e, minute)}
                  onMouseLeave={handleTranscriptMouseLeave}
                  title="View transcript"
                >
                  {minute.minute}
                </span>
                <div className="minute-header-right">
                  {minute.sentiment && minute.sentiment.length > 0 ? (
                    <span 
                      className="minute-total-words-pill"
                      onMouseEnter={(e) => handleSentimentMouseEnter(e, minute)}
                      onMouseLeave={handleSentimentMouseLeave}
                      title="View sentiment analysis"
                    >
                      {minute.totalWords} words
                    </span>
                  ) : (
                    <span className="minute-total-words">
                      {minute.totalWords} words
                    </span>
                  )}
                </div>
              </div>
              
              <div className="minute-speakers">
                {speakers.map(speaker => {
                  const speakerData = minute.speakerWords.find(s => s.speaker === speaker);
                  const wordCount = speakerData ? speakerData.totalWords : 0;
                  const useWhiteText = shouldUseWhiteText(wordCount, minute.totalWords);
                  
                  return (
                    <div key={speaker} className="speaker-row">
                      <span className="speaker-name" title={speaker}>{speaker}</span>
                      <div className="speaker-words-bar">
                        <div 
                          className="speaker-words-fill"
                          style={{ 
                            width: `${(wordCount / minute.totalWords) * 100}%`,
                            opacity: wordCount > 0 ? 1 : 0.3
                          }}
                        ></div>
                        <span 
                          className={`speaker-word-count ${useWhiteText ? 'speaker-word-count-white' : 'speaker-word-count-black'}`}
                        >
                          {wordCount}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="minute-indicators">
                <div className="minute-indicators-row">
                  {/* First row: All objectives and coaching points if they fit, otherwise first portion */}
                  {minute.objectives.map((objective, index) => (
                    <TimelineBubble
                      key={`objective-${minute.minute}-${index}`}
                      type="objective"
                      data={[objective]}
                      minute={minute.minute}
                      data-testid={`timeline-bubble-objective-${minute.minute}-${index}`}
                    />
                  ))}
                  {minute.coachingPoints.map((point, index) => (
                    <TimelineBubble
                      key={`coaching-${minute.minute}-${index}`}
                      type="coaching"
                      data={[point]}
                      minute={minute.minute}
                      data-testid={`timeline-bubble-coaching-${minute.minute}-${index}`}
                    />
                  ))}
                  {/* Empty state for first row if no content */}
                  {minute.objectives.length === 0 && minute.coachingPoints.length === 0 && (
                    <span className="minute-indicators-empty">Objectives and coaching points will appear here</span>
                  )}
                </div>
                  {/* Second row only shows if there are too many items for the first row */}
                  {(minute.objectives.length + minute.coachingPoints.length) > 6 && (
                    <div className="minute-indicators-row">
                      {/* Show overflow items on second row if needed */}
                      {minute.objectives.slice(6).map((objective, index) => (
                        <TimelineBubble
                          key={`objective-overflow-${minute.minute}-${index}`}
                          type="objective"
                          data={[objective]}
                          minute={minute.minute}
                          data-testid={`timeline-bubble-objective-overflow-${minute.minute}-${index}`}
                        />
                      ))}
                      {minute.coachingPoints.slice(Math.max(0, 6 - minute.objectives.length)).map((point, index) => (
                        <TimelineBubble
                          key={`coaching-overflow-${minute.minute}-${index}`}
                          type="coaching"
                          data={[point]}
                          minute={minute.minute}
                          data-testid={`timeline-bubble-coaching-overflow-${minute.minute}-${index}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Transcript Hover */}
      {showTranscriptHover && currentTranscriptMinute && currentTranscriptMinute.transcript && (
        <TimelineTranscriptHover
          minute={currentTranscriptMinute.minute}
          transcript={currentTranscriptMinute.transcript}
          position={transcriptHoverPosition}
          onClose={handleTranscriptClose}
          onMouseEnter={handleTranscriptHoverEnter}
          onMouseLeave={handleTranscriptHoverLeave}
        />
      )}
      
      {/* Sentiment Hover */}
      {showSentimentHover && currentSentimentMinute && currentSentimentMinute.sentiment && (
        <TimelineSentimentHover
          minute={currentSentimentMinute.minute}
          sentiment={currentSentimentMinute.sentiment}
          position={sentimentHoverPosition}
          onClose={handleSentimentClose}
          onMouseEnter={handleSentimentHoverEnter}
          onMouseLeave={handleSentimentHoverLeave}
        />
      )}
      
    </div>
  );
}

export default Timeline;
